import { observer } from "mobx-react-lite";
import { useCallback } from "react";
import { DatabaseIcon, FunctionSquareIcon, ServerIcon } from "lucide-react";

import FunctionDropDown from "./FunctionDropdown";
import HeadersSection from "./HeadersSection";
import {
  useFunctionSelection,
  useHeadersManagement,
  useMCPFunctionSchema,
  useServerUrlInput,
} from "./hooks";
import InputDataSources from "./InputDataSources";
import ServerUrlInput from "./ServerUrlInput";

import { MCPFunction, useListMCPFunctions } from "@/queries/evals/useListMCPFunctions";
import { MCPConfiguration } from "@/types/evaluate";
import { isValidUrl } from "@/utils/utils";
import { BlockProps } from "@/components/PowerBlock/Block";
import { useBlock, withBlockStore } from "@/components/PowerBlock/Block/block-store";
import { createEmptySources } from "@/components/PowerBlock/Block/hooks";
import { useIsReadonly } from "@/components/PowerBlock/Block/readonly-context";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import SourcesField from "@/components/PowerBlock/Block/SourcesField";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { required, validUrl } from "@/components/PowerBlock/Block/validation/rules";
import { createBlockSchema } from "@/components/PowerBlock/Block/validation/schemaBuilder";

const validationSchema = createBlockSchema<MCPConfiguration>()
  .requireName()
  .addRule(required("configuration.server_configuration.server_url", "Server URL is required"))
  .addRule(validUrl("configuration.server_configuration.server_url", "Must be a valid URL"))
  .addRule(required("configuration.function_name", "Function selection is required"))
  .build();

const MCPBlockCore = observer(
  ({ sources = createEmptySources() }: Omit<BlockProps<MCPConfiguration>, "DefaultFields">) => {
    const blockStore = useBlock<MCPConfiguration>();
    const isReadonly = useIsReadonly();

    // Derive values from configuration
    const serverUrl = blockStore.configuration?.server_configuration?.server_url || "";
    const headers = blockStore.configuration?.server_configuration?.headers || {};
    const functionName = blockStore.configuration?.function_name || null;
    const inputData = blockStore.configuration?.input_data || [];

    const { handleUrlChange } = useServerUrlInput(blockStore);
    const { handleHeadersChange } = useHeadersManagement(blockStore, serverUrl);

    const { handleInputDataChange, inputDataMapping } = useFunctionSelection(blockStore, inputData);

    const functionsQuery = useListMCPFunctions(serverUrl, headers);
    const { mcpSchema } = useMCPFunctionSchema(functionsQuery.data, functionName);

    const handleFunctionSelect = useCallback(
      (mcpFunction: MCPFunction) => {
        blockStore.setConfiguration({
          ...blockStore.configuration,
          function_name: mcpFunction.name,
        } as MCPConfiguration);
      },
      [blockStore],
    );

    return (
      <>
        <SectionGroup
          title="Server Configuration"
          description="Configure the MCP server connection and authentication"
          icon={<ServerIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <ValidatedField name="Server URL" path="configuration.server_configuration.server_url">
              <ServerUrlInput
                serverUrl={serverUrl}
                onUrlChange={handleUrlChange}
                disabled={isReadonly}
              />
            </ValidatedField>

            <ValidatedField name="Auth Headers" path="">
              <HeadersSection
                headers={headers}
                onHeadersChange={handleHeadersChange}
                disabled={isReadonly}
              />
            </ValidatedField>
          </div>
        </SectionGroup>

        {serverUrl && isValidUrl(serverUrl) && (
          <>
            <SectionGroup
              title="Function Selection"
              description="Choose which MCP function to execute"
              icon={<FunctionSquareIcon className="h-4 w-4" />}
            >
              <ValidatedField path="configuration.function_name" variant="sources">
                <FunctionDropDown
                  serverUrl={serverUrl}
                  headers={headers}
                  selectedFunction={functionName}
                  onFunctionSelect={handleFunctionSelect}
                  disabled={isReadonly}
                />
              </ValidatedField>
            </SectionGroup>

            {mcpSchema && mcpSchema.properties && Object.keys(mcpSchema.properties).length > 0 && (
              <SectionGroup
                title="Input Data Sources"
                description="Map function parameters to data sources"
                icon={<DatabaseIcon className="h-4 w-4" />}
              >
                <SourcesField>
                  <InputDataSources
                    inputDataMapping={inputDataMapping}
                    sources={sources}
                    handleInputDataChange={handleInputDataChange}
                    disabled={isReadonly}
                  />
                </SourcesField>
              </SectionGroup>
            )}
          </>
        )}
      </>
    );
  },
);

export default withBlockStore<MCPConfiguration>(MCPBlockCore, {
  validationSchema,
});
